import { cn } from '@repo/utils/cn';
import { type ElementType } from 'react';
import Link from '@/components/common/link';

type SimpleListDataItem = {
  id?: string;
  name?: string;
  Icon?: ElementType;
  href: string;
  className?: string;
  iconClassName?: string;
};

type SimpleListData = {
  id: string;
  title?: string;
  className?: string;
  items: SimpleListDataItem[];
};

type SimpleListProps = {
  data: SimpleListData;
  containerClassName?: string;
};

const SimpleList = ({ data, containerClassName }: SimpleListProps) => {
  const { id, title, items, className } = data;

  return (
    <div
      data-testid={id}
      className={cn('flex-grow sm:ltr:text-left sm:rtl:text-right', containerClassName)}
    >
      {title && (
        <h4 className="mb-1 block text-[16px] font-bold vt-font-highlight-font-family vt-headings-text">{title}</h4>
      )}
      <div className={className ?? 'inline-table items-center justify-center sm:block'}>
        {items.map(({ name, Icon, href, className: linkClassName, iconClassName }, idx) => (
          <Link
            key={(name ?? href) + idx}
            href={href}
            target={/^https?:\/\//.test(href) ? '_blank' : undefined}
            rel={/^https?:\/\//.test(href) ? 'noopener noreferrer' : undefined}
            className={cn(
              'group flex cursor-pointer items-center space-x-1 pt-1.5 rtl:space-x-reverse',
              items.length > 1 && 'ltr:pr-2.5 rtl:pl-2.5',
              linkClassName
            )}
          >
            {name && (
              <span
                className={cn(
                  'flex vt-atoms-link-text vt-text-size-medium sm:vt-text-size-medium-sm',
                  'transition-colors duration-300 ease-in-out',
                  'group-hover:vt-utility-white'
                )}
              >
                {name}
              </span>
            )}
            {Icon && (
              <div
                title={href?.replace('https://', '')?.replace('www.', '')}
                className="group flex items-center rounded-full p-[1px] transition-all duration-300 ease-in-out hover:vt-atoms-btn-primary-bg"
              >
                <Icon
                  className={cn(
                    'size-7 flex-shrink-0 transform-gpu transition-transform duration-500 ease-in-out group-hover:rotate-[360deg] sm:size-9',
                    iconClassName
                  )}
                />
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default SimpleList;
